import { Box, Button, Card, CardContent, Chip, CircularProgress, Skeleton, Typography } from '@mui/material'
import { FC } from 'react'
import { HCPArteKanbanColumnKeyEnum } from '../../Services/HCP/creativeflow/creative-flow.interface'
import IKanbanBoardHCPArtesSchemaProps from './interfaces/kanban-board-hcp-artworks-schema.interface'
import { KanbanCardArte } from './components/kanban-art-card.component'
import { FiltroAprovadoOSType, FiltroItensPendentesType } from '../../Pages/HCP/ArtProgress/art-progress.hook'

interface IKanbanBoardHCPArtesProps {
  data?: IKanbanBoardHCPArtesSchemaProps
  loading?: boolean
  onCardClick?: (taskId: string) => void
  filtroAprovadoOS?: FiltroAprovadoOSType
  onFiltroAprovadoOSChange?: (value: FiltroAprovadoOSType) => void
  aprovadoHasMore?: boolean
  aprovadoTotal?: number
  aprovadoCarregados?: number
  semOSCarregados?: number
  comOSCarregados?: number
  semOSTotal?: number
  comOSTotal?: number
  loadingMore?: boolean
  onLoadMore?: () => void
  filtroItensPendentes?: FiltroItensPendentesType
  onFiltroItensPendentesChange?: (value: FiltroItensPendentesType) => void
}

const COLUMN_BG: Record<HCPArteKanbanColumnKeyEnum, string> = {
  [HCPArteKanbanColumnKeyEnum.ARTES_NAO_INICIADAS]: '#e3f2fd',
  [HCPArteKanbanColumnKeyEnum.ARTE_EM_ANDAMENTO]: '#fff8e1',
  [HCPArteKanbanColumnKeyEnum.REVISAO_CLIENTE]: '#f3e5f5',
  [HCPArteKanbanColumnKeyEnum.ALTERACAO_EM_ANDAMENTO]: '#fce4ec',
  [HCPArteKanbanColumnKeyEnum.APROVADO_FINAL]: '#e8f5e9',
}

const COLUMN_COLOR: Record<HCPArteKanbanColumnKeyEnum, string> = {
  [HCPArteKanbanColumnKeyEnum.ARTES_NAO_INICIADAS]: '#1565c0',
  [HCPArteKanbanColumnKeyEnum.ARTE_EM_ANDAMENTO]: '#f57f17',
  [HCPArteKanbanColumnKeyEnum.REVISAO_CLIENTE]: '#6a1b9a',
  [HCPArteKanbanColumnKeyEnum.ALTERACAO_EM_ANDAMENTO]: '#c62828',
  [HCPArteKanbanColumnKeyEnum.APROVADO_FINAL]: '#2e7d32',
}

const FILTRO_OS_LABELS: Record<FiltroAprovadoOSType, string> = {
  todos: 'Todos',
  sem_os: 'Sem OS',
  com_os: 'Com OS',
}

const FILTRO_PENDENTES_LABELS: Record<FiltroItensPendentesType, string> = {
  todos: 'Todos',
  completos: 'Pedido completo',
  com_pendencias: 'Com pendências',
}

const KanbanBoardHCPArtes: FC<IKanbanBoardHCPArtesProps> = ({
  data, loading, onCardClick,
  filtroAprovadoOS = 'todos', onFiltroAprovadoOSChange,
  aprovadoHasMore, aprovadoTotal, aprovadoCarregados,
  semOSCarregados, comOSCarregados, semOSTotal, comOSTotal,
  loadingMore, onLoadMore,
  filtroItensPendentes = 'todos', onFiltroItensPendentesChange,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', alignItems: 'flex-start', pb: 2 }}>
        {Object.values(COLUMN_BG).map((bgColor, i) => (
          <Box key={i} sx={{ minWidth: 280, maxWidth: 300, flexShrink: 0 }}>
            {/* Header skeleton */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, px: 0.5 }}>
              <Skeleton variant="text" width={130} height={20} />
              <Skeleton variant="circular" width={20} height={20} />
            </Box>
            {/* Coluna skeleton */}
            <Box
              sx={{
                background: bgColor,
                borderRadius: 2,
                padding: 1.5,
                height: 'calc(100vh - 290px)',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              {Array.from({ length: i === 4 ? 3 : 4 }).map((_, j) => (
                <Box key={j} sx={{ bgcolor: '#fff', borderRadius: 1, p: 1.5, boxShadow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Skeleton variant="text" width="40%" height={16} />
                    <Skeleton variant="rounded" width={32} height={16} sx={{ borderRadius: 1 }} />
                  </Box>
                  <Skeleton variant="text" width="65%" height={14} />
                  <Skeleton variant="text" width="50%" height={14} />
                  {j % 2 === 0 && (
                    <Skeleton variant="rounded" width="100%" height={60} sx={{ mt: 0.5, borderRadius: 1 }} />
                  )}
                  <Skeleton variant="rounded" width={90} height={18} sx={{ mt: 0.75, borderRadius: 1 }} />
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    )
  }

  if (!data || !data.columnOrder || data.columnOrder.length === 0) {
    return (
      <Box sx={{ padding: '20px', textAlign: 'center' }}>
        <Typography variant="h6">Nenhuma arte encontrada.</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', alignItems: 'flex-start', pb: 2 }}>
      {data.columnOrder.map((columnId) => {
        const column = data.columns[columnId]
        if (!column) return null

        const allTasks = column.taskIds.map((id) => data.tasks[id]).filter(Boolean)
        const bgColor = COLUMN_BG[columnId]
        const headerColor = COLUMN_COLOR[columnId]
        const isAprovadoCol = columnId === HCPArteKanbanColumnKeyEnum.APROVADO_FINAL

        return (
          <Box key={column.id} sx={{ minWidth: 280, maxWidth: 300, flexShrink: 0 }}>

            {/* Cabeçalho da coluna */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: isAprovadoCol ? 0.75 : 1.5, px: 0.5 }}>
              <Typography variant="subtitle2" fontWeight="bold" color={headerColor}>
                {column.title}
              </Typography>
              <Chip
                label={column.count}
                size="small"
                sx={{ bgcolor: headerColor, color: '#fff', fontWeight: 700, fontSize: '0.70rem', height: 20 }}
              />
            </Box>

            {/* Filtros exclusivos da coluna Aprovado Final */}
            {isAprovadoCol && (
              <>
                {onFiltroAprovadoOSChange && (
                  <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, px: 0.5 }}>
                    {(['todos', 'sem_os', 'com_os'] as FiltroAprovadoOSType[]).map((v) => (
                      <Chip
                        key={v}
                        label={FILTRO_OS_LABELS[v]}
                        size="small"
                        onClick={() => onFiltroAprovadoOSChange(v)}
                        sx={{
                          fontSize: '0.65rem',
                          height: 20,
                          cursor: 'pointer',
                          fontWeight: filtroAprovadoOS === v ? 700 : 400,
                          bgcolor: filtroAprovadoOS === v ? headerColor : 'rgba(0,0,0,0.06)',
                          color: filtroAprovadoOS === v ? '#fff' : 'text.secondary',
                          '&:hover': { opacity: 0.85 },
                        }}
                      />
                    ))}
                  </Box>
                )}
                {onFiltroItensPendentesChange && (
                  <Box sx={{ display: 'flex', gap: 0.5, mb: 1, px: 0.5 }}>
                    {(['todos', 'completos', 'com_pendencias'] as FiltroItensPendentesType[]).map((v) => (
                      <Chip
                        key={v}
                        label={FILTRO_PENDENTES_LABELS[v]}
                        size="small"
                        onClick={() => onFiltroItensPendentesChange(v)}
                        sx={{
                          fontSize: '0.60rem',
                          height: 18,
                          cursor: 'pointer',
                          fontWeight: filtroItensPendentes === v ? 700 : 400,
                          bgcolor: filtroItensPendentes === v
                            ? (v === 'completos' ? '#2e7d32' : v === 'com_pendencias' ? '#e65100' : headerColor)
                            : 'rgba(0,0,0,0.06)',
                          color: filtroItensPendentes === v ? '#fff' : 'text.secondary',
                          '&:hover': { opacity: 0.85 },
                        }}
                      />
                    ))}
                  </Box>
                )}
              </>
            )}

            {/* Cards — scroll interno por coluna */}
            <Box
              sx={{
                background: bgColor,
                borderRadius: 2,
                padding: 1.5,
                height: 'calc(100vh - 290px)',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                '&::-webkit-scrollbar': { width: 4 },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { background: '#ccc', borderRadius: 2 },
              }}
            >
              {allTasks.map((task) => {
                const temOS = isAprovadoCol && task.content.tem_os === true
                return (
                <Card
                  key={task.id}
                  onClick={() => onCardClick && onCardClick(task.id)}
                  sx={{
                    flexShrink: 0,
                    cursor: onCardClick ? 'pointer' : 'default',
                    boxShadow: 1,
                    border: task.content.atrasado
                      ? '1px solid #d32f2f'
                      : temOS
                      ? '1px solid #1565c0'
                      : '1px solid transparent',
                    bgcolor: temOS ? '#f0f7ff' : 'inherit',
                    '&:hover': onCardClick ? { boxShadow: 3, transform: 'translateY(-1px)', transition: 'all 0.15s ease' } : {},
                  }}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <KanbanCardArte task={task} />
                  </CardContent>
                </Card>
                )
              })}

              {allTasks.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="caption" color="text.disabled">
                    Nenhum item nesta coluna
                  </Typography>
                </Box>
              )}

              {/* Ver mais — exclusivo da coluna Aprovado Final */}
              {isAprovadoCol && (aprovadoHasMore || loadingMore) && (
                <Box sx={{ pt: 1, pb: 0.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                  {/* Contador detalhado: mostra breakdown quando filtro OS é "todos" */}
                  {filtroAprovadoOS === 'todos' && semOSCarregados !== undefined && comOSCarregados !== undefined ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', display: 'block' }}>
                        {`${semOSCarregados} sem OS`}
                        {semOSTotal !== undefined && ` de ${semOSTotal}`}
                        {` · ${comOSCarregados} com OS`}
                        {comOSTotal !== undefined && ` de ${comOSTotal}`}
                      </Typography>
                      {aprovadoTotal !== undefined && (
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.60rem' }}>
                          {`${(semOSCarregados ?? 0) + (comOSCarregados ?? 0)} de ${aprovadoTotal} carregados no total`}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    aprovadoTotal !== undefined && (
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', textAlign: 'center' }}>
                        {`${aprovadoCarregados ?? allTasks.length} de ${aprovadoTotal} carregados`}
                        {filtroAprovadoOS === 'sem_os' && ' (sem OS)'}
                        {filtroAprovadoOS === 'com_os' && ' (com OS)'}
                      </Typography>
                    )
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={onLoadMore}
                    disabled={loadingMore}
                    startIcon={loadingMore ? <CircularProgress size={12} /> : undefined}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600, width: '100%' }}
                  >
                    {loadingMore ? 'Carregando...' : 'Ver mais'}
                  </Button>
                </Box>
              )}
            </Box>

          </Box>
        )
      })}
    </Box>
  )
}

export { KanbanBoardHCPArtes }
